// src/utils/services/clinicalAiPanelsService.js
import { callOpenAIChat } from './openaiClient';
import { SECTION_KEYS } from '../../pages/ClinicalDataCollector/models/clinicalAiModels';

// Helper: igual patrón que en hccAiService
const safeParseJsonFromModel = (rawContent) => {
  if (!rawContent) {
    throw new Error('No content to parse');
  }

  let text = Array.isArray(rawContent)
    ? rawContent
        .map((p) =>
          typeof p === 'string'
            ? p
            : (p && (p.text || p.content || '')) || ''
        )
        .join('')
    : String(rawContent);

  text = text.trim();

  // Limpiar ```json ... ``` o ``` ... ```
  if (text.startsWith('```')) {
    text = text.replace(/^```[a-zA-Z]*\s*/m, '');
    text = text.replace(/```$/m, '');
    text = text.trim();
  }

  const firstBrace = text.indexOf('{');
  const lastBrace = text.lastIndexOf('}');

  if (firstBrace === -1 || lastBrace === -1 || lastBrace <= firstBrace) {
    console.error('[safeParseJsonFromModel] No JSON object found in text:', text);
    throw new Error('No JSON object found in model response');
  }

  const jsonSlice = text.slice(firstBrace, lastBrace + 1);
  return JSON.parse(jsonSlice);
};

const EMPTY_RESULT = {
  generatedAt: new Date().toISOString(),
  diagnoses: [],
  sectionsSummary: {
    [SECTION_KEYS.HYPERTENSION]: false,
    [SECTION_KEYS.CV_RISK]: false,
    [SECTION_KEYS.AF]: false,
    [SECTION_KEYS.HEART_FAILURE]: false,
    [SECTION_KEYS.COPD]: false,
    [SECTION_KEYS.ASTHMA]: false,
  },
  sectionsData: {},
};

/**
 * Genera los paneles clínicos (6 secciones) desde IA.
 * Recibe contexto clínico completo (demographics + notas + meds + labs + allergies).
 *
 * @param {Object} args
 * @param {string} args.contextText  Texto completo del contexto clínico del paciente
 * @returns {Promise<{
 *  generatedAt: string;
 *  diagnoses: any[];
 *  sectionsSummary: any;
 *  sectionsData: any;
 * }>}
 */
export const generateClinicalAiPanelsFromAI = async ({ contextText }) => {
  if (!contextText || contextText.trim().length === 0) {
    console.warn('[generateClinicalAiPanelsFromAI] No clinical context text');
    return { ...EMPTY_RESULT, generatedAt: new Date().toISOString() };
  }

  const systemMessage = `You are a cardiology and internal medicine specialist assisting a primary care physician.

Your role is to analyze REAL clinical data for ONE adult patient and identify specific chronic conditions.

CRITICAL RULES:
- ONLY use diagnoses that are EXPLICITLY documented in the clinical data provided.
- DO NOT invent, assume, or hallucinate diagnoses that are not clearly present in the data.
- Base your analysis on documented vitals (BP readings, BMI), lab results, medications, clinical notes, and assessments.
- If a section does not apply based on the data, set its flag to false and its data to null.
- Return ONLY a valid JSON object, no additional text.`;

  const userMessage = `Analyze the following patient clinical data and classify documented diagnoses into these sections:
- "${SECTION_KEYS.HYPERTENSION}" (hypertension, elevated blood pressure)
- "${SECTION_KEYS.CV_RISK}" (global cardiovascular risk, lipid disorders, DM, smoking, etc.)
- "${SECTION_KEYS.AF}" (atrial fibrillation / flutter)
- "${SECTION_KEYS.HEART_FAILURE}" (heart failure)
- "${SECTION_KEYS.COPD}" (chronic obstructive pulmonary disease)
- "${SECTION_KEYS.ASTHMA}" (asthma)
- "${SECTION_KEYS.OTHER}" (all other diagnoses not mapped above)

Return a JSON object with this exact structure:

{
  "generatedAt": "YYYY-MM-DDTHH:mm:ssZ",
  "diagnoses": [
    {
      "label": "Diagnosis name",
      "icd10": "ICD-10 code",
      "section": "sectionKey",
      "sourceSnippet": "short text excerpt from the data that supports this dx",
      "confidence": 0.9
    }
  ],
  "sectionsSummary": {
    "hypertension": true/false,
    "cvRisk": true/false,
    "af": true/false,
    "heartFailure": true/false,
    "copd": true/false,
    "asthma": true/false
  },
  "sectionsData": {
    "hypertension": {
      "bpDistribution": [{ "category": "Normal (<120/80)", "count": 3 }],
      "nnt": [{ "outcome": "MI prevention over 10 years", "value": 45 }],
      "keyRecommendations": ["string"],
      "sharedDecisionPoints": ["string"]
    },
    "cvRisk": {
      "riskPercent": 18.5,
      "statinPlan": "string",
      "additionalTherapies": ["string"]
    },
    "af": {
      "code": "I48.0",
      "description": "string",
      "rhythmControl": ["string"],
      "rateControl": ["string"],
      "anticoagulation": ["string"],
      "appendageClosure": ["string"],
      "benefitConsiderations": ["string"]
    },
    "heartFailure": {
      "code": "I50.9",
      "description": "string",
      "nyhaClass": "string",
      "labs": [{ "test": "BNP", "result": "string", "date": "string" }],
      "immunizations": ["string"],
      "treatment": ["string"],
      "lifestyle": ["string"]
    },
    "copd": {
      "code": "J44.1",
      "description": "string",
      "goldClassification": "string",
      "spirometry": [{ "measure": "FEV1", "value": "string", "date": "string" }],
      "immunizations": ["string"],
      "maintenanceTherapy": ["string"],
      "exacerbationHistory": { "count": 0, "lastDate": "string" },
      "nonPharma": ["string"]
    },
    "asthma": {
      "code": "J45.30",
      "description": "string",
      "severity": "string",
      "controlScores": { "ACT": 0, "ACQ": 0 },
      "spirometry": [{ "measure": "FEV1", "value": "string", "date": "string" }],
      "maintenanceTherapy": ["string"],
      "rescueTherapy": ["string"],
      "triggers": ["string"],
      "managementPlans": ["string"]
    }
  }
}

Set inactive sections to null in sectionsData and false in sectionsSummary.

===== PATIENT CLINICAL DATA =====

${contextText}`;

  const data = await callOpenAIChat({
    model: 'gpt-4o-mini',
    messages: [
      { role: 'system', content: systemMessage },
      { role: 'user', content: userMessage },
    ],
    temperature: 0.15,
  });

  const content = data?.choices?.[0]?.message?.content;
  console.log('[generateClinicalAiPanelsFromAI] RAW content from OpenAI:', content);

  if (!content) {
    console.error('[generateClinicalAiPanelsFromAI] Empty response from OpenAI');
    throw new Error('Empty response from OpenAI');
  }

  try {
    const json = safeParseJsonFromModel(content);

    return {
      generatedAt: json.generatedAt || new Date().toISOString(),
      diagnoses: Array.isArray(json.diagnoses) ? json.diagnoses : [],
      sectionsSummary: json.sectionsSummary || {
        [SECTION_KEYS.HYPERTENSION]: false,
        [SECTION_KEYS.CV_RISK]: false,
        [SECTION_KEYS.AF]: false,
        [SECTION_KEYS.HEART_FAILURE]: false,
        [SECTION_KEYS.COPD]: false,
        [SECTION_KEYS.ASTHMA]: false,
      },
      sectionsData: json.sectionsData || {},
    };
  } catch (err) {
    console.error('[generateClinicalAiPanelsFromAI] Error parsing JSON:', err);
    return { ...EMPTY_RESULT, generatedAt: new Date().toISOString() };
  }
};
